import { useState, useCallback } from 'react';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getQuotationRequestsApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { 
  QuotationRequest, 
  SearchQuotationRequestsRequest, 
  ACLAuthorizationRequest,
  QuotationRequestResponse,
  SearchQuotationRequestsPostContentTypeEnum
} from '@/api/bol';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseQuotationRequestsReturn {
  quotationRequests: QuotationRequestResponse[];
  loading: boolean;
  searchError: string | null;
  selectedQuotationRequest: QuotationRequestResponse | null;
  setSelectedQuotationRequest: (quotationRequest: QuotationRequestResponse | null) => void;
  fetchQuotationRequests: (page?: number, pageSize?: number, searchBody?: SearchQuotationRequestsRequest) => Promise<boolean>;
  createQuotationRequest: (quotationRequestData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteQuotationRequest: (quotationRequestId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useQuotationRequests = (): UseQuotationRequestsReturn => {
  const [quotationRequests, setQuotationRequests] = useState<QuotationRequestResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedQuotationRequest, setSelectedQuotationRequest] = useState<QuotationRequestResponse | null>(null);
  const [total, setTotal] = useState(0);
  
  const { showSuccess, showError } = useSnackbarNotification();

  const fetchQuotationRequests = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchQuotationRequestsRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);

    console.log('Fetching quotation requests with:', { page, pageSize, searchBody: normalizedSearchBody });

    setLoading(true);
    setSearchError(null);
    
    try {
      const api = getQuotationRequestsApi();
      
      const response = await api.searchQuotationRequestsPost({
        contentType: SearchQuotationRequestsPostContentTypeEnum.ApplicationJson,
        searchQuotationRequestsRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize
      });
      
      console.log('QuotationRequests API response:', response);
      
      setQuotationRequests(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      console.error('Error fetching quotation requests:', err);
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch quotation requests');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createQuotationRequest = useCallback(async (quotationRequestData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      console.log('Creating quotation request with data:', quotationRequestData, 'Member:', memberIdentifier, 'ACL:', aclAuthorizations);
      
      const api = getQuotationRequestsApi();
      
      const createdQuotationRequest = await api.createQuotationRequest({
        contentType: SearchQuotationRequestsPostContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        quotationRequest: quotationRequestData as QuotationRequest,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined
      });
      
      console.log('QuotationRequest created successfully:', createdQuotationRequest);
      const createdQuotationRequestId = createdQuotationRequest?.id;
      
      showSuccess(
        'QuotationRequest created successfully',
        createdQuotationRequestId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('quotation-request', createdQuotationRequestId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      console.error('Error creating quotation request:', err);
      const errorMessage = await formatOperationError(err, 'Create quotation request', 'Failed to create quotation request');
      showError(errorMessage);
      throw err; // Re-throw to let the calling component handle form state
    }
  }, [showSuccess, showError]);

  const deleteQuotationRequest = useCallback(async (quotationRequestId: string) => {
    try {
      const api = getQuotationRequestsApi();
      
      await api.deleteQuotationRequestById({
        quotationRequestId
      });
      
      showSuccess('QuotationRequest deleted successfully');
    } catch (err: unknown) {
      console.error('Error deleting quotation request:', err);
      const errorMessage = await formatOperationError(err, 'Delete quotation request', 'Failed to delete quotation request');
      showError(errorMessage);
      throw err; // Re-throw to let the calling component handle dialog state
    }
  }, [showSuccess, showError]);

  return {
    quotationRequests,
    loading,
    searchError,
    selectedQuotationRequest,
    setSelectedQuotationRequest,
    fetchQuotationRequests,
    createQuotationRequest,
    deleteQuotationRequest,
    setTotal,
    total
  };
};